import React, { useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GitBranch, Split, Trash2 } from 'lucide-react';

import {
  allocationStatusColor,
  allocationStatusLabel,
  allocationStatusOptions,
  commitmentLineLabel,
  numberValue,
  productLabel,
} from './selloutUtils';

const confidenceLabel = (value) => {
  if (value === 'high') return 'Alta';
  if (value === 'medium') return 'Media';
  if (value === 'low') return 'Baja';
  if (value === 'manual') return 'Manual';
  return value || '-';
};

const productName = (row, productById) => {
  const product = row.real_product || productById.get(Number(row.real_product_id));
  return product ? productLabel(product) : `Producto #${row.real_product_id || '-'}`;
};

export default function SellOutAllocationPreview({
  allocations,
  onAllocationsChange,
  unassignedLines,
  onUnassignedChange,
  commitmentLines,
  receiptLines,
  warnings,
  disabled,
}) {
  const productById = useMemo(() => {
    const map = new Map();
    (receiptLines || []).forEach((line) => {
      if (line.real_product_id && line.real_product) {
        map.set(Number(line.real_product_id), line.real_product);
      }
    });
    return map;
  }, [receiptLines]);

  const updateAllocation = (clientId, patch) => {
    onAllocationsChange((allocations || []).map((row) => (
      row.client_id === clientId ? { ...row, ...patch } : row
    )));
  };

  const removeAllocation = (clientId) => {
    onAllocationsChange((allocations || []).filter((row) => row.client_id !== clientId));
  };

  const splitAllocation = (row) => {
    const qty = numberValue(row.received_qty);
    if (qty <= 1) return;
    const firstQty = Math.floor(qty / 2);
    const secondQty = qty - firstQty;
    onAllocationsChange((allocations || []).flatMap((item) => {
      if (item.client_id !== row.client_id) return [item];
      return [
        { ...item, received_qty: firstQty, accepted_qty: firstQty, rejected_qty: 0 },
        {
          ...item,
          client_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          commitment_line_id: null,
          received_qty: secondQty,
          accepted_qty: secondQty,
          rejected_qty: 0,
          confidence: 'manual',
          allocation_status: 'requires_review',
          reason: 'Division manual para asignacion multiple.',
        },
      ];
    }));
  };

  const updateUnassigned = (clientId, patch) => {
    onUnassignedChange((unassignedLines || []).map((row) => (
      row.client_id === clientId ? { ...row, ...patch } : row
    )));
  };

  const assignUnassigned = (row) => {
    if (!row.commitment_line_id) return;
    const qty = numberValue(row.received_qty);
    onAllocationsChange([
      ...(allocations || []),
      {
        ...row,
        received_qty: qty,
        accepted_qty: numberValue(row.accepted_qty) || qty,
        rejected_qty: numberValue(row.rejected_qty),
        confidence: 'manual',
        allocation_status: 'requires_review',
        reason: row.reason || 'Asignacion manual desde linea sin sugerencia.',
      },
    ]);
    onUnassignedChange((unassignedLines || []).filter((item) => item.client_id !== row.client_id));
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Preview de asignacion</Typography>
          <Typography variant="body2" color="text.secondary">
            Revise las sugerencias y confirme la linea de promesa antes de guardar.
          </Typography>
        </Box>
      </Stack>

      {(warnings || []).length > 0 ? (
        <Alert severity="warning">
          {(warnings || []).join(' ')}
        </Alert>
      ) : null}

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell>Producto real</TableCell>
              <TableCell width={110} align="right">Cantidad</TableCell>
              <TableCell>Linea de promesa</TableCell>
              <TableCell width={110}>Confianza</TableCell>
              <TableCell width={150}>Estado</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell width={90} align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(allocations || []).map((row) => {
              const selectedLine = (commitmentLines || []).find((line) => Number(line.id) === Number(row.commitment_line_id)) || null;
              return (
                <TableRow key={row.client_id} sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <Typography variant="body2" title={productName(row, productById)} noWrap>
                      {productName(row, productById)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={row.received_qty || ''}
                      disabled={disabled}
                      onChange={(event) => updateAllocation(row.client_id, {
                        received_qty: event.target.value,
                        accepted_qty: event.target.value,
                      })}
                      inputProps={{ min: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={commitmentLines || []}
                      value={selectedLine}
                      disabled={disabled}
                      onChange={(_, line) => updateAllocation(row.client_id, {
                        commitment_line_id: line?.id || null,
                        allocation_status: line ? 'confirmed' : 'requires_review',
                      })}
                      getOptionLabel={commitmentLineLabel}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      renderInput={(params) => <TextField {...params} size="small" label="Linea" />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{confidenceLabel(row.confidence)}</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.allocation_status || 'confirmed'}
                      disabled={disabled}
                      onChange={(event) => updateAllocation(row.client_id, { allocation_status: event.target.value })}
                    >
                      {allocationStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Chip
                      size="small"
                      sx={{ mt: 0.75 }}
                      label={allocationStatusLabel(row.allocation_status)}
                      color={allocationStatusColor(row.allocation_status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.25} justifyContent="center">
                      <Tooltip title="Dividir">
                        <span>
                          <IconButton size="small" onClick={() => splitAllocation(row)} disabled={disabled || numberValue(row.received_qty) <= 1}>
                            <Split size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Quitar">
                        <span>
                          <IconButton size="small" color="error" onClick={() => removeAllocation(row.client_id)} disabled={disabled}>
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {(unassignedLines || []).length > 0 ? (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Requieren asignacion manual
          </Typography>
          <Stack spacing={1}>
            {(unassignedLines || []).map((row) => {
              const selectedLine = (commitmentLines || []).find((line) => Number(line.id) === Number(row.commitment_line_id)) || null;
              return (
                <Stack
                  key={row.client_id}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ md: 'center' }}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                >
                  <Box sx={{ minWidth: 220, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap title={productName(row, productById)}>
                      {productName(row, productById)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.reason || 'Sin sugerencia clara'}
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Cantidad"
                    value={row.received_qty || ''}
                    disabled={disabled}
                    onChange={(event) => updateUnassigned(row.client_id, {
                      received_qty: event.target.value,
                      accepted_qty: event.target.value,
                    })}
                    inputProps={{ min: 1 }}
                    sx={{ width: { md: 130 } }}
                  />
                  <Autocomplete
                    options={commitmentLines || []}
                    value={selectedLine}
                    disabled={disabled}
                    onChange={(_, line) => updateUnassigned(row.client_id, { commitment_line_id: line?.id || null })}
                    getOptionLabel={commitmentLineLabel}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => <TextField {...params} size="small" label="Linea de promesa" />}
                    sx={{ minWidth: { md: 330 }, flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<GitBranch size={16} />}
                    onClick={() => assignUnassigned(row)}
                    disabled={disabled || !row.commitment_line_id || numberValue(row.received_qty) <= 0}
                  >
                    Asignar
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}
